import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';

/**
 * Custom hook for fetching maintenance data
 */
export const useMaintenanceData = () => {
  const [maintenance, setMaintenance] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [completedWorkOrders, setCompletedWorkOrders] = useState([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [upcomingMaintenance, setUpcomingMaintenance] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [refreshingUpcoming, setRefreshingUpcoming] = useState(false);
  const { showToast } = useToast();
  const upcomingAbortRef = useRef(null);
  const hasUpcomingLoadedRef = useRef(false);

  const fetchMaintenance = async () => {
    try {
      const response = await axios.get('/api/maintenance');
      setMaintenance(response.data);
    } catch (error) {
      console.error('Error fetching maintenance:', error);
    }
  };

  const fetchAircraft = async () => {
    try {
      const response = await axios.get('/api/aircraft');
      setAircraft(response.data);
    } catch (error) {
      console.error('Error fetching aircraft:', error);
    }
  };

  const fetchCompletedWorkOrders = useCallback(async () => {
    setLoadingCompleted(true);
    try {
      const response = await axios.get('/api/maintenance', { params: { status: 'COMPLETED' } });
      setCompletedWorkOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching completed work orders:', error);
      showToast('Failed to fetch work order history', 'error');
      setCompletedWorkOrders([]);
    } finally {
      setLoadingCompleted(false);
    }
  }, [showToast]);

  const fetchUpcomingMaintenance = async (
    days,
    aircraftFilter,
    typeFilter,
    rangeEnabled = { days: false, hours: false, cycles: false, landings: false },
    hours = 100,
    cycles = 500,
    landings = 250
  ) => {
    const useDays = !!rangeEnabled?.days;
    const useHours = !!rangeEnabled?.hours;
    const useCycles = !!rangeEnabled?.cycles;
    const useLandings = !!rangeEnabled?.landings;
    if (
      (useDays && days === 'custom') ||
      (useHours && hours === 'custom') ||
      (useCycles && cycles === 'custom') ||
      (useLandings && landings === 'custom')
    ) {
      return;
    }

    if (upcomingAbortRef.current) {
      upcomingAbortRef.current.abort();
    }
    const controller = new AbortController();
    upcomingAbortRef.current = controller;

    const isRefresh = hasUpcomingLoadedRef.current;
    if (isRefresh) {
      setRefreshingUpcoming(true);
    } else {
      setLoadingUpcoming(true);
    }
    try {
      const noRangeFilter = !useDays && !useHours && !useCycles && !useLandings;
      const params = {
        useDays: useDays || noRangeFilter ? '1' : '0',
        useHours: useHours ? '1' : '0',
        useCycles: useCycles ? '1' : '0',
        useLandings: useLandings ? '1' : '0'
      };
      if (noRangeFilter) {
        // No day/hours/cycles/landings filter — return all rows
        params.unbounded = '1';
      } else {
        params.pastDays = 90;
      }
      if (useDays) {
        params.days = typeof days === 'number' ? days : 90;
        params.pastDays = params.pastDays ?? 90;
      }
      if (useHours) {
        params.hours = typeof hours === 'number' ? hours : 100;
      }
      if (useCycles) {
        params.cycles = typeof cycles === 'number' ? cycles : 500;
      }
      if (useLandings) {
        params.landings = typeof landings === 'number' ? landings : 250;
      }
      if (aircraftFilter !== 'ALL') {
        params.aircraftId = aircraftFilter;
      }
      if (typeFilter !== 'ALL') {
        params.type = typeFilter;
      }
      const response = await axios.get('/api/maintenance/upcoming', {
        params,
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setUpcomingMaintenance(response.data.items || []);
      hasUpcomingLoadedRef.current = true;
    } catch (error) {
      if (controller.signal.aborted || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching upcoming maintenance:', error);
      showToast('Failed to fetch upcoming maintenance', 'error');
      setUpcomingMaintenance([]);
      hasUpcomingLoadedRef.current = true;
    } finally {
      if (!controller.signal.aborted) {
        setLoadingUpcoming(false);
        setRefreshingUpcoming(false);
      }
    }
  };

  useEffect(() => {
    fetchMaintenance();
    fetchAircraft();
    return () => {
      upcomingAbortRef.current?.abort();
    };
  }, []);

  return {
    maintenance,
    aircraft,
    completedWorkOrders,
    loadingCompleted,
    upcomingMaintenance,
    loadingUpcoming,
    refreshingUpcoming,
    fetchMaintenance,
    fetchAircraft,
    fetchCompletedWorkOrders,
    fetchUpcomingMaintenance,
    setMaintenance
  };
};
